import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_STRING as api } from '@/App';
import { toast } from 'sonner';

import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/auth/PasswordInput';
import { Button } from '@/components/ui/button';
import { Lock } from 'lucide-react';
export default function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [challengeId, setChallengeId] = useState(null); // non-null = 2FA code step
  const [code, setCode] = useState('');
  const navigate = useNavigate();

  const finishLogin = (data) => {
    if (!data.user.is_admin) {
      toast.error('Access denied: Admin privileges required');
      return;
    }
    // Token is set by backend as httpOnly cookie; only cache user data.
    localStorage.setItem('user', JSON.stringify(data.user));
    onLogin(data.user);
    toast.success('Admin login successful');
    navigate('/admin/dashboard');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${api}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      if (data.requires_2fa) {
        setChallengeId(data.challenge_id);
        toast.info(data.message);
        return;
      }

      finishLogin(data);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${api}/auth/verify-2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ challenge_id: challengeId, code })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Verification failed');
      }

      finishLogin(data);
    } catch (error) {
      toast.error(error.message);
      if (String(error.message).includes('sign in again')) {
        setChallengeId(null);
        setCode('');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center admin-area" style={{ background: '#FFFAF5' }}>
      <div className="w-full max-w-md p-8 rounded-2xl" style={{ background: 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <div className="flex items-center justify-center mb-6">
          <div className="p-3 rounded-full" style={{ background: '#FED7A5' }}>
            <Lock className="w-6 h-6" style={{ color: '#20212B' }} />
          </div>
        </div>
        
        <h1 className="text-2xl font-bold text-center mb-2" style={{ color: '#20212B' }}>
          {challengeId ? 'Check Your Email' : 'Admin Login'}
        </h1>
        <p className="text-center mb-6" style={{ color: '#73766A' }}>
          {challengeId ? 'Enter the 6-digit code we just sent you' : 'Mementa Dashboard'}
        </p>

        {challengeId ? (
          <form onSubmit={handleVerifyCode} className="space-y-4" data-testid="admin-2fa-form">
            <Input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              required
              autoFocus
              disabled={loading}
              data-testid="admin-2fa-code-input"
              className="text-center text-2xl tracking-[0.5em] font-mono"
            />
            <p className="text-xs text-center" style={{ color: '#73766A' }}>
              The code expires in 10 minutes. Check spam if you don't see it.
            </p>
            <Button
              type="submit"
              className="w-full"
              disabled={loading || code.length !== 6}
              data-testid="admin-2fa-verify-button"
              style={{ background: '#9E6752', color: 'white', padding: '12px', borderRadius: '8px' }}
            >
              {loading ? 'Verifying...' : 'Verify & Sign In'}
            </Button>
            <button
              type="button"
              onClick={() => { setChallengeId(null); setCode(''); }}
              data-testid="admin-2fa-back-button"
              className="w-full text-sm"
              style={{ color: '#9E6752' }}
            >
              ← Back to login
            </button>
          </form>
        ) : (
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#534145' }}>
              Email
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@mementa.com"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#534145' }}>
              Password
            </label>
            <PasswordInput
              testId="admin-login-password-input"
              toggleTestId="admin-login-password-toggle"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={loading}
            style={{
              background: '#9E6752',
              color: 'white',
              padding: '12px',
              borderRadius: '8px'
            }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>
        )}

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-sm"
            style={{ color: '#9E6752' }}
          >
            ← Back to site
          </button>
        </div>
      </div>
    </div>
  );
}
